/**
 * 资产库（P-D②，蓝图 §4「tab: 创作资产 | 主体资产 | 分镜 | 回收站」）。
 *
 * 跨画布聚合：创作资产（图/视频/音频）+ 主体资产（角色/场景/道具）+ 分镜 + 回收站。
 * **回收站是全系统统一入口**（软删除/恢复/彻底删除，纯前端本地），各功能区不再各建回收站。
 * 数据来自 canvasLibrary.collectAssets；删除→进回收站（隐藏），恢复→回库，彻底删除→从来源画布移除节点。
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clapperboard, Film, Image as ImageIcon, Music, RotateCcw, Search, Trash2, Users, X } from 'lucide-react';

import { collectAssets, loadCanvas, removeNodeFromCanvas, type AssetBundle, type MediaAsset, type ShotAsset, type SubjectAsset } from '../../features/canvas/canvasLibrary';
import { useCanvasStore } from '../../features/canvas/canvasStore';
import { useTrashStore, trashKey } from '../trashStore';
import { Modal } from '../../ui/Modal';
import { DotMatrixBackground } from '../../ui/DotMatrixBackground';

type Tab = 'media' | 'subject' | 'shot' | 'trash';
const CAT_LABEL: Record<SubjectAsset['category'], string> = { character: '角色', scene: '场景', prop: '道具' };
const KIND_LABEL: Record<MediaAsset['kind'], string> = { image: '图片', video: '视频', audio: '音频' };

const chip = 'rounded-control px-2.5 py-1 text-uiSm transition-colors';
const chipOn = 'bg-accent/15 font-semibold text-accent';
const chipOff = 'bg-surface-base text-ink-secondary hover:text-ink-primary';

export function AssetLibraryPage() {
  const navigate = useNavigate();
  const [bundle, setBundle] = useState<AssetBundle>(() => collectAssets());
  const [tab, setTab] = useState<Tab>('media');
  const [typeFilter, setTypeFilter] = useState<'all' | MediaAsset['kind']>('all');
  const [srcFilter, setSrcFilter] = useState<string>('all');
  const [catFilter, setCatFilter] = useState<'all' | SubjectAsset['category']>('all');
  const [preview, setPreview] = useState<MediaAsset | SubjectAsset | null>(null);
  const trash = useTrashStore();

  useEffect(() => {
    setBundle(collectAssets());
  }, []);

  const isTrashed = (canvasId: string, nodeId: string): boolean => trash.items.some((i) => i.key === trashKey(canvasId, nodeId));
  const liveMedia = bundle.media.filter((m) => !isTrashed(m.canvasId, m.nodeId));
  const liveSubjects = bundle.subjects.filter((s) => !isTrashed(s.canvasId, s.nodeId));
  const liveShots = bundle.shots.filter((s) => !isTrashed(s.canvasId, s.nodeId));

  const sources = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of bundle.media) {
      map.set(m.canvasId, m.canvasTitle);
    }
    return [...map.entries()];
  }, [bundle]);

  const media = liveMedia.filter((m) => (typeFilter === 'all' || m.kind === typeFilter) && (srcFilter === 'all' || m.canvasId === srcFilter));
  const subjects = liveSubjects.filter((s) => catFilter === 'all' || s.category === catFilter);

  const isMedia = (p: MediaAsset | SubjectAsset): p is MediaAsset => 'url' in p && 'kind' in p;

  function openShot(s: ShotAsset): void {
    const doc = loadCanvas(s.canvasId);
    if (doc !== null) {
      useCanvasStore.getState().setDoc(doc);
      navigate('/canvas/editor');
    }
  }
  function purge(item: { canvasId: string; nodeId: string; key: string }): void {
    removeNodeFromCanvas(item.canvasId, item.nodeId);
    trash.purge(item.key);
    setBundle(collectAssets());
  }

  return (
    <div className="relative h-full overflow-y-auto">
      <DotMatrixBackground className="opacity-50" />
      <div className="relative mx-auto flex min-h-full w-full max-w-[1760px] flex-col px-6 py-8">
        <header className="mb-5">
          <h1 className="text-2xl font-semibold tracking-tight">资产库</h1>
          <p className="mt-1 text-ui text-ink-muted">跨画布统一管理已生成的素材、主体与分镜；删除的资产统一进「回收站」。</p>
        </header>

        {/* tab */}
        <div className="mb-4 flex items-center gap-1.5">
          {(
            [
              { k: 'media', label: `创作资产 ${liveMedia.length}` },
              { k: 'subject', label: `主体资产 ${liveSubjects.length}` },
              { k: 'shot', label: `分镜 ${liveShots.length}` },
              { k: 'trash', label: `回收站 ${trash.items.length}` },
            ] as { k: Tab; label: string }[]
          ).map((t) => (
            <button key={t.k} type="button" onClick={() => setTab(t.k)} className={`${chip} ${tab === t.k ? chipOn : chipOff}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'media' && (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-1.5">
              {(['all', 'image', 'video', 'audio'] as const).map((k) => (
                <button key={k} type="button" onClick={() => setTypeFilter(k)} className={`${chip} ${typeFilter === k ? chipOn : chipOff}`}>
                  {k === 'all' ? '全部类型' : KIND_LABEL[k]}
                </button>
              ))}
              <span className="mx-1 h-4 w-px bg-surface-line" />
              <select value={srcFilter} onChange={(e) => setSrcFilter(e.target.value)} className="rounded-control bg-surface-base px-2 py-1 text-uiSm text-ink-primary outline-none">
                <option value="all">全部来源</option>
                {sources.map(([id, title]) => (
                  <option key={id} value={id}>{title}</option>
                ))}
              </select>
            </div>
            {media.length === 0 ? (
              <EmptyState text="暂无创作资产。在画布里生成图片/视频/音频后，会自动汇总到这里。" />
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8">
                {media.map((m) => (
                  <div key={`${m.canvasId}-${m.nodeId}`} className="panel group relative flex flex-col overflow-hidden">
                    <button type="button" onClick={() => setPreview(m)} className="flex flex-col text-left">
                      <div className="flex h-28 items-center justify-center overflow-hidden bg-surface-base">
                        {m.kind === 'image' && <img src={m.thumbnail ?? m.url} alt="" className="h-full w-full object-cover" />}
                        {m.kind === 'video' && (m.thumbnail !== undefined ? <img src={m.thumbnail} alt="" className="h-full w-full object-cover" /> : <Film size={22} className="text-ink-faint" />)}
                        {m.kind === 'audio' && <Music size={22} className="text-ink-faint" />}
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1.5">
                        <span className="truncate text-uiSm text-ink-primary">{m.title}</span>
                        <span className="ml-auto shrink-0 text-[10px] text-ink-faint">{KIND_LABEL[m.kind]}</span>
                      </div>
                    </button>
                    <TrashBtn onClick={() => trash.add({ key: trashKey(m.canvasId, m.nodeId), canvasId: m.canvasId, canvasTitle: m.canvasTitle, nodeId: m.nodeId, kind: 'media', label: m.title, thumb: m.thumbnail, url: m.url })} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'subject' && (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-1.5">
              {(['all', 'character', 'scene', 'prop'] as const).map((k) => (
                <button key={k} type="button" onClick={() => setCatFilter(k)} className={`${chip} ${catFilter === k ? chipOn : chipOff}`}>
                  {k === 'all' ? '全部主体' : CAT_LABEL[k]}
                </button>
              ))}
            </div>
            {subjects.length === 0 ? (
              <EmptyState text="暂无主体资产。从导演剧本「生成资产卡」或在画布新建角色/场景/道具卡后汇聚于此。" />
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8">
                {subjects.map((s) => (
                  <div key={`${s.canvasId}-${s.nodeId}`} className="panel group relative flex flex-col overflow-hidden">
                    <button type="button" onClick={() => setPreview(s)} className="flex flex-col text-left">
                      <div className="flex h-28 items-center justify-center overflow-hidden bg-surface-base">
                        {s.thumb !== undefined ? <img src={s.thumb} alt="" className="h-full w-full object-cover" /> : <Users size={22} className="text-ink-faint" />}
                      </div>
                      <div className="px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          <span className="truncate text-uiSm font-semibold text-ink-primary">{s.name}</span>
                          <span className="ml-auto shrink-0 rounded bg-accent/15 px-1 text-[10px] text-accent">{CAT_LABEL[s.category]}</span>
                        </div>
                        {s.description !== '' && <p className="mt-0.5 line-clamp-2 text-[11px] text-ink-muted">{s.description}</p>}
                      </div>
                    </button>
                    <TrashBtn onClick={() => trash.add({ key: trashKey(s.canvasId, s.nodeId), canvasId: s.canvasId, canvasTitle: s.canvasTitle, nodeId: s.nodeId, kind: 'subject', label: s.name, thumb: s.thumb })} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'shot' && (
          liveShots.length === 0 ? (
            <EmptyState text="暂无分镜。在导演剧本卡「展开分镜」后，逐镜会汇总到这里。" />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8">
              {liveShots.map((s) => (
                <div key={`${s.canvasId}-${s.nodeId}`} className="panel group relative flex flex-col gap-1 overflow-hidden p-3">
                  <button type="button" onClick={() => openShot(s)} className="flex flex-col gap-1 text-left">
                    <div className="flex h-16 items-center justify-center rounded-control bg-surface-base">
                      <Clapperboard size={20} className="text-ink-faint" />
                    </div>
                    <span className="truncate text-uiSm font-semibold text-ink-primary">{s.title}</span>
                    <span className="truncate text-[11px] text-ink-muted">来源：{s.canvasTitle}</span>
                  </button>
                  <TrashBtn onClick={() => trash.add({ key: trashKey(s.canvasId, s.nodeId), canvasId: s.canvasId, canvasTitle: s.canvasTitle, nodeId: s.nodeId, kind: 'shot', label: s.title })} />
                </div>
              ))}
            </div>
          )
        )}

        {tab === 'trash' && (
          <>
            {trash.items.length > 0 && (
              <div className="mb-3 flex justify-end">
                <button type="button" onClick={() => trash.clear()} className="btn-ghost inline-flex items-center gap-1 px-2.5 py-1 text-uiSm">
                  <X size={13} />
                  清空回收站
                </button>
              </div>
            )}
            {trash.items.length === 0 ? (
              <div className="panel flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
                <Trash2 size={26} className="text-ink-faint" />
                <p className="text-ui text-ink-secondary">回收站为空</p>
                <p className="max-w-sm text-uiSm text-ink-muted">在资产库删除的素材会先进这里，可恢复或彻底删除。</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {trash.items.map((it) => (
                  <div key={it.key} className="panel flex items-center gap-3 p-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-control bg-surface-base">
                      {it.thumb !== undefined ? <img src={it.thumb} alt="" className="h-full w-full object-cover" /> : it.kind === 'shot' ? <Clapperboard size={16} className="text-ink-faint" /> : <ImageIcon size={16} className="text-ink-faint" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-ui text-ink-primary">{it.label}</div>
                      <div className="truncate text-uiSm text-ink-faint">来源：{it.canvasTitle} · {it.kind === 'media' ? '创作资产' : it.kind === 'subject' ? '主体' : '分镜'}</div>
                    </div>
                    <button type="button" onClick={() => trash.restore(it.key)} className="btn-ghost inline-flex items-center gap-1 px-2.5 py-1 text-uiSm">
                      <RotateCcw size={13} />
                      恢复
                    </button>
                    <button type="button" onClick={() => purge(it)} className="inline-flex items-center gap-1 rounded-control px-2.5 py-1 text-uiSm text-state-danger hover:bg-state-danger/10">
                      <Trash2 size={13} />
                      彻底删除
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={preview !== null ? ('name' in preview ? preview.name : preview.title) : '预览'}
        summary={preview !== null && 'canvasTitle' in preview ? `来源：${preview.canvasTitle}` : '资产预览'}
        footer={
          <button type="button" className="btn-ghost px-3 py-1.5 text-ui" onClick={() => setPreview(null)}>
            关闭
          </button>
        }
      >
        {preview !== null && (
          <div className="flex flex-col items-center gap-3">
            {isMedia(preview) ? (
              preview.kind === 'audio' ? (
                <audio src={preview.url} controls className="w-full" />
              ) : preview.kind === 'video' ? (
                <video src={preview.url} poster={preview.thumbnail} controls className="max-h-[60vh] w-full rounded-control bg-black" />
              ) : (
                <img src={preview.url} alt="" className="max-h-[60vh] w-auto rounded-control" />
              )
            ) : (
              <>
                {preview.thumb !== undefined && <img src={preview.thumb} alt="" className="max-h-[50vh] w-auto rounded-control" />}
                <p className="text-uiSm text-ink-muted">{CAT_LABEL[preview.category]} · {preview.description}</p>
              </>
            )}
            {isMedia(preview) && (
              <p className="flex items-center gap-1 text-uiSm text-ink-faint">
                <Search size={12} /> 来源：{preview.canvasTitle}
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function TrashBtn({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="删除到回收站"
      onClick={onClick}
      className="absolute right-1 top-1 z-10 rounded bg-black/55 p-1 text-white opacity-0 transition-opacity hover:text-state-danger group-hover:opacity-100"
    >
      <Trash2 size={12} />
    </button>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="panel flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
      <ImageIcon size={26} className="text-ink-faint" />
      <p className="max-w-md text-uiSm text-ink-muted">{text}</p>
    </div>
  );
}
